/**
 * PresentCardListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ModalLayout,
  InputFormTable,
  Text,
} from 'components';
import {
  Button,
  Flex,
  Image,
  Input,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import { STORE_URL } from 'utils';
import { MinusOutlined, SearchOutlined } from '@ant-design/icons';
import { downloadXlsx } from 'utils/XlsxDownloader';
import { PRESENT_YES_OR_NO } from 'constants/PresentContants';

const INITIAL_FILTER_ITEM = {
  category_name: null,
  title: null,
};

const PresentCardListPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    presentCards,
    inputValue,
    isLoading,
    onChange,
    onSubmit,
    modalType,
    onModal,
  } = props;

  /* ===== STATE ===== */
  /* ===== VARIABLE ===== */
  const fileInputRef = React.useRef(null);
  const [filterItem, setFilterItem] = React.useState(INITIAL_FILTER_ITEM);
  const [sortItems, setSortItems] = React.useState([]);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  /**
   * 폼
   * --
   */
  const form = [
    {
      label: '타이틀',
      required: true,
      form: [
        {
          input: (
            <Input
              placeholder="타이틀을 입력해주세요"
              style={{ width: 400 }}
              value={inputValue.title}
              onChange={(e) => onChange('title', e.target.value)}
              maxLength={30}
              showCount
            />
          ),
        },
      ],
    },
    {
      label: '카테고리명',
      required: true,
      form: [
        {
          input: (
            <Input
              placeholder="카테고리 이름을 입력해주세요"
              style={{ width: 400 }}
              value={inputValue.category_name}
              onChange={(e) => onChange('category_name', e.target.value)}
              maxLength={30}
              showCount
            />
          ),
        },
      ],
    },
    {
      label: '파일',
      required: true,
      form: [
        {
          input: (
            <>
              {inputValue.image_url ? (
                typeof inputValue.image_url === 'string' ? (
                  <div style={{ position: 'relative', top: 0, left: 0 }}>
                    <Image
                      width={300}
                      src={`${STORE_URL}/${inputValue.image_url}`}
                    />

                    <Button
                      danger
                      size="small"
                      icon={<MinusOutlined />}
                      style={{ position: 'absolute', top: -10, right: -10 }}
                      onClick={() => onChange('image_url', null)}
                    />
                  </div>
                ) : (
                  <div style={{ position: 'relative', top: 0, left: 0 }}>
                    <Image
                      width={300}
                      src={URL.createObjectURL(inputValue.image_url)}
                    />

                    <Button
                      danger
                      size="small"
                      icon={<MinusOutlined />}
                      style={{ position: 'absolute', top: -10, right: -10 }}
                      onClick={() => onChange('image_url', null)}
                    />
                  </div>
                )
              ) : (
                <React.Fragment>
                  <input
                    ref={fileInputRef}
                    id="upload"
                    type="file"
                    multiple={true}
                    accept={'image/*'}
                    onChange={(e) => onChange('image_url', e.target.files[0])}
                  />
                  <Button onClick={() => fileInputRef.current.click()}>
                    파일선택
                  </Button>
                </React.Fragment>
              )}
              <p style={{ color: '#D33444' }}>
                * 이미지 비율은 3:2로 업로드 해주세요.
              </p>
            </>
          ),
        },
      ],
    },
    {
      label: '노출여부',
      form: [
        {
          input: (
            <Radio.Group
              value={inputValue.is_show}
              onChange={(e) => onChange('is_show', e.target.value)}
            >
              <Radio value={'Y'}>사용</Radio>
              <Radio value={'N'}>사용안함</Radio>
            </Radio.Group>
          ),
        },
      ],
    },
  ];

  /**
   * 테이블 컬럼
   * --
   */
  const columns = [
    {
      title: 'No',
      dataIndex: 'present_card_id',
      key: 'present_card_id',
      width: 30,
      sorter: (a, b) => a?.present_card_id - b?.present_card_id,
    },
    {
      title: '카테고리명',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 95,
      render: (category_name) => <>{category_name}</>,
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={filterItem?.category_name}
            onChange={(e) =>
              setFilterItem((prev) => ({
                ...prev,
                category_name: e.target.value,
              }))
            }
            onPressEnter={() => confirm()}
            placeholder={`카테고리 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setFilterItem((prev) => ({
                  ...prev,
                  category_name: null,
                }));
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: '카드',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 100,
      render: (image_url) => (
        <Image
          src={`${STORE_URL}/${image_url}`}
          width={80}
          height={50}
          preview={false}
        />
      ),
    },
    {
      title: '타이틀',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={filterItem?.title}
            onChange={(e) =>
              setFilterItem((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            onPressEnter={() => confirm()}
            placeholder={`카테고리 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setFilterItem((prev) => ({
                  ...prev,
                  title: null,
                }));
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: '노출여부',
      dataIndex: 'is_show',
      key: 'is_show',
      width: 40,
      align: 'center',
      render: (text) => (
        <Tag color={text === 'Y' ? 'processing' : 'default'}>
          {text === 'Y' ? '노출' : '숨김'}
        </Tag>
      ),
      filters: Object.entries(PRESENT_YES_OR_NO).map(([key, v]) => ({
        text: v['SHOW'].text,
        value: key,
      })),
      onFilter: (value, { is_show }) => is_show === value,
    },
    {
      title: '노출순서',
      dataIndex: 'order',
      key: 'order',
      width: 40,
      align: 'center',
      sorter: (a, b) => a?.order - b?.order,
    },
    {
      title: '등록일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      align: 'center',
      render: (text) => dayjs.unix(text).format('YYYY.MM.DD HH:mm'),
      sorter: (a, b) => a?.created_at - b?.created_at,
    },
  ];

  /**
   * 카드 필터
   * --
   */
  const filterList = React.useMemo(() => {
    let arr = presentCards;

    const { category_name, title } = filterItem;

    if (category_name) {
      arr = arr.filter((item) => item.category_name.includes(category_name));
    }

    if (title) {
      arr = arr.filter((item) => item.title.includes(title));
    }
    return arr;
  }, [presentCards, filterItem]);
  /* ===== FUNCTION ===== */

  /**
   * 아이템 순서 변경 (0-based index 기준)
   * @param {object} child - 순서를 변경할 대상 객체 (예: { present_card_id, order, ... })
   * @param {number} newIndex - 목표 인덱스 (0부터 시작)
   */
  const orderChange = (child, newIndex) => {
    // sortItems는 객체 배열이어야 합니다.
    // 우선 기존 배열의 복사본을 생성합니다.
    const newArr = [...filterList];

    // 대상 객체의 현재 인덱스를 찾습니다.
    // banner_id나 다른 고유값을 기준으로 찾는 것을 권장합니다.
    const currentIndex = newArr.findIndex(
      (item) => item.present_card_id === child.present_card_id
    );
    if (currentIndex === -1) return; // 대상 객체를 찾지 못하면 종료
    if (currentIndex === newIndex) return; // 인덱스가 동일하면 변경할 필요 없음

    // 현재 인덱스에서 대상 아이템 제거
    const [movedItem] = newArr.splice(currentIndex, 1);
    // 목표 인덱스 위치에 대상 아이템 삽입
    newArr.splice(newIndex, 0, movedItem);

    // 배열 내의 모든 객체에 대해 order 값을 재할당 (1부터 시작하도록)
    const updatedList = newArr.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // 최종 updatedList는 객체 배열 형태로 저장됩니다.
    setSortItems(updatedList);
  };

  /* ===== HOOKS ===== */

  /**
   * 순서정렬 아이템 정제
   * --
   */
  React.useEffect(() => {
    if (modalType !== 'SORT') return;

    const filterItems = presentCards?.sort((a, b) => a?.order - b?.order);

    setSortItems(filterItems);
  }, [presentCards, modalType]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>선물 카드 관리</Title>

              {/* 검색결과 */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <Title
                  size={5}
                  subButton={
                    <div>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() => onModal('create')}
                      >
                        새 선물 카드
                      </Button>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() => onModal('sort', filterList)}
                      >
                        순서변경
                      </Button>
                      <Button
                        style={{ marginRight: 5 }}
                        onClick={() =>
                          downloadXlsx({
                            columns: columns,
                            rows: filterList,
                            fileName: '선물 카드',
                          })
                        }
                      >
                        엑셀로 다운로드
                      </Button>
                    </div>
                  }
                >
                  <span>
                    총 <span>{filterList.length}</span>개
                  </span>
                </Title>
                <br />

                <Table
                  dataSource={filterList}
                  columns={columns}
                  scroll={{
                    x: 'max-content',
                  }}
                  onRow={(record) => {
                    return {
                      onClick: () => onModal('detail', record),
                    };
                  }}
                />
              </Content>
              {/* 검색결과 끝 */}
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* SECTION: 상세정보 모달 */}
      <ModalLayout
        title="선물카드 상세정보"
        type={'modal'}
        open={modalType && modalType !== 'SORT'}
        onCancel={() => onModal('close')}
        centered
        closable
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={8} style={{ textAlign: 'left', display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: '133%',
                      paddingRight: 5,
                    }}
                  >
                    노출여부
                  </p>
                  <Switch
                    value={inputValue.is_show === 'Y' ? true : false}
                    onChange={(e) => onChange('is_show', e ? 'Y' : 'N')}
                  />
                </div>
              </Col>
              <Col x={8} style={{ textAlign: 'center' }}></Col>
              <Col x={8}>
                {modalType !== 'CREATE' ? (
                  <Button
                    danger
                    style={{
                      ...styles.button,
                    }}
                    onClick={() => onSubmit('delete')}
                  >
                    삭제
                  </Button>
                ) : null}

                <Button
                  type="primary"
                  style={{
                    ...styles.button,
                    background: 'var(--primary-color)',
                  }}
                  onClick={() =>
                    onSubmit(modalType === 'CREATE' ? 'create' : 'update')
                  }
                >
                  저장
                </Button>
              </Col>
            </Row>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'10px 0'}>
          <Row>
            <Col x={24}>
              <InputFormTable form={form} labelWidth={148} />
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 순서변경 모달 */}
      <ModalLayout
        type={'modal'}
        open={modalType && modalType === 'SORT'}
        onCancel={() => onModal('close')}
        centered
        closable
        width={500}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={24}>
                <Flex justify="flex-end">
                  <Button
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => onSubmit('sort', sortItems)}
                  >
                    저장
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'20px 0'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Flex vertical gap={24} style={{ padding: '12px 0px' }}>
                  {sortItems?.map((_item, _index) => (
                    <Flex
                      key={`banner-${_item.banner_id}`}
                      justify="space-between"
                      align="center"
                    >
                      <Flex align="center" gap={12}>
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #BDBDBD',
                          }}
                        >
                          <Image
                            src={`${STORE_URL}/${_item?.image_url}`}
                            width={80}
                            height={'100%'}
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                        <Text weight={700}>{_item?.title}</Text>
                      </Flex>

                      <Select
                        value={_index}
                        options={new Array(sortItems?.length)
                          .fill(null)
                          .map((_, index) => ({
                            label: `${index + 1}번`,
                            value: index,
                          }))}
                        onChange={(e) => orderChange(_item, Number(e))}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </>
  );
};

export default PresentCardListPresenter;
